import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { NumericInputBase } from '@/features/contract/components/field-renderer/inputs/NumericInputBase'
import { formatPlNumber } from '@/features/contract/components/numberFormat'
import type { ScheduleTemplateScope } from '../api'
import { useDeleteScheduleTemplate, useSaveScheduleTemplate, useScheduleTemplate } from '../hooks'
import { SCHEDULE_BASIS_LABELS, type ScheduleBasis } from '../types'

type Row = { label: string; percent: number | null; basis: ScheduleBasis; offsetDays: number | null }

const inputCls =
  'rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm shadow-xs focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15'
const btnCls =
  'inline-flex items-center justify-center rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white shadow-xs hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed'

const BASIS_OPTIONS = Object.keys(SCHEDULE_BASIS_LABELS) as ScheduleBasis[]

/** Edytor szablonu harmonogramu - per konfiguracja (fallback) lub per produkt (nadpisanie). */
export function CashScheduleTemplateEditor({ scope, code }: { scope: ScheduleTemplateScope; code: string }) {
  const templateQ = useScheduleTemplate(scope, code)
  const save = useSaveScheduleTemplate(scope, code)
  const del = useDeleteScheduleTemplate(scope, code)
  const confirm = useConfirm()

  const [label, setLabel] = useState('')
  const [rows, setRows] = useState<Row[]>([])

  useEffect(() => {
    const t = templateQ.data
    if (!t) return
    setLabel(t.label ?? '')
    setRows(t.items.map(i => ({ label: i.label, percent: i.percent, basis: i.basis, offsetDays: i.offsetDays })))
  }, [templateQ.data])

  if (templateQ.isLoading) return <p className="text-sm text-slate-500">Ładowanie szablonu…</p>
  if (templateQ.isError) return <p className="text-sm text-rose-700">Nie udało się załadować szablonu.</p>

  const percentSum = rows.reduce((s, r) => s + (r.percent ?? 0), 0)
  const balanced = Math.abs(percentSum - 100) < 0.005

  const setRow = (idx: number, patch: Partial<Row>) =>
    setRows(rs => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  const addRow = () =>
    setRows(rs => [...rs, { label: '', percent: null, basis: 'SIGNING', offsetDays: 0 }])
  const removeRow = (idx: number) => setRows(rs => rs.filter((_, i) => i !== idx))

  const serverHasTemplate = !!(templateQ.data && (templateQ.data.label || templateQ.data.items.length > 0))

  const doDelete = async () => {
    const ok = await confirm({
      title: 'Usunąć szablon?',
      description: scope === 'products'
        ? 'Harmonogram tego produktu będzie generowany z fallbacku (szablon konfiguracji).'
        : 'Konfiguracja nie będzie miała własnego szablonu.',
      confirmLabel: 'Usuń',
      variant: 'destructive',
    })
    if (!ok) return
    del.mutate(undefined, {
      onSuccess: () => { setLabel(''); setRows([]); toast.success('Szablon usunięty') },
      onError: () => toast.error('Nie udało się usunąć szablonu'),
    })
  }

  const doSave = () => {
    if (!label.trim()) { toast.error('Podaj nazwę szablonu'); return }
    if (rows.length === 0) { toast.error('Dodaj co najmniej jedną transzę'); return }
    if (rows.some(r => !r.label.trim())) { toast.error('Każda transza musi mieć etykietę'); return }
    if (!balanced) { toast.error(`Procenty muszą sumować się do 100% (jest ${formatPlNumber(percentSum, 2)}%)`); return }
    const items = rows.map((r, i) => ({
      seq: i + 1,
      label: r.label.trim(),
      percent: r.percent ?? 0,
      basis: r.basis,
      offsetDays: r.offsetDays,
    }))
    save.mutate({ label: label.trim(), isActive: true, items }, {
      onSuccess: () => toast.success('Szablon zapisany'),
      onError: () => toast.error('Nie udało się zapisać szablonu'),
    })
  }

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <p className="text-xs text-slate-500">
        {scope === 'products'
          ? 'Szablon dla tego typu produktu - nadpisuje szablon konfiguracji przy generowaniu harmonogramu.'
          : 'Szablon dla tej konfiguracji - używany dla produktów, które nie mają własnego szablonu (fallback).'}
      </p>

      <div className="flex flex-col gap-1 max-w-sm">
        <label className="text-[11px] font-medium text-slate-600">Nazwa szablonu</label>
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="np. Domyślny 30/50/20" className={inputCls} />
      </div>

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/60 text-left text-slate-600 text-xs">
            <tr>
              <th className="px-3 py-2 font-semibold">#</th>
              <th className="px-3 py-2 font-semibold">Etykieta</th>
              <th className="px-3 py-2 font-semibold text-right">%</th>
              <th className="px-3 py-2 font-semibold">Podstawa terminu</th>
              <th className="px-3 py-2 font-semibold text-right">Offset (dni)</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-4 text-xs text-slate-500">Brak transz - dodaj poniżej.</td></tr>
            ) : rows.map((r, idx) => (
              <tr key={idx} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-400">{idx + 1}</td>
                <td className="px-3 py-2">
                  <input value={r.label} onChange={e => setRow(idx, { label: e.target.value })}
                    placeholder="np. Zaliczka" className={`${inputCls} w-full`} />
                </td>
                <td className="px-3 py-2 w-36">
                  <NumericInputBase value={r.percent} onChange={v => setRow(idx, { percent: v as number | null })}
                    decimals={2} suffix="%" />
                </td>
                <td className="px-3 py-2">
                  <select value={r.basis} onChange={e => setRow(idx, { basis: e.target.value as ScheduleBasis })} className={inputCls}>
                    {BASIS_OPTIONS.map(b => <option key={b} value={b}>{SCHEDULE_BASIS_LABELS[b]}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 w-32">
                  <NumericInputBase value={r.offsetDays} onChange={v => setRow(idx, { offsetDays: v as number | null })}
                    decimals={0} suffix="dni" />
                </td>
                <td className="px-3 py-2 text-right">
                  <button type="button" className="text-rose-600 hover:text-rose-800 font-medium"
                    onClick={() => removeRow(idx)} title="Usuń transzę">✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" className="text-sm text-sky-700 hover:text-sky-900 font-medium" onClick={addRow}>
          + Dodaj transzę
        </button>
        <span className={`text-sm font-medium ${balanced ? 'text-emerald-700' : 'text-amber-700'}`}>
          Suma: {formatPlNumber(percentSum, 2)}% {balanced ? '✓' : '(powinno być 100%)'}
        </span>
      </div>

      <div className="flex items-center gap-4">
        <button type="button" className={btnCls} onClick={doSave} disabled={save.isPending}>
          Zapisz szablon
        </button>
        {serverHasTemplate && (
          <button type="button" className="text-sm font-medium text-rose-600 hover:text-rose-800 disabled:opacity-50"
            onClick={doDelete} disabled={del.isPending}>
            Usuń szablon
          </button>
        )}
      </div>
    </div>
  )
}
